// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the presenter's camera and microphone in a meeting
 * (`docs/SHARED_AR_PLAN.md`), through Cloudflare Realtime: `goLive`
 * publishes them from the presenter's page, `watch` pulls them into
 * everyone else's.
 *
 * WebRTC to Realtime's SFU, negotiated through the site's own routes
 * (`functions/api/meeting/media`), which hold the app secret:
 *
 *   publish — the browser offers its tracks (send only), the route opens
 *             a Realtime session, publishes them, returns the answer,
 *             and tells the room the presenter is live;
 *   watch   — the route opens a session that pulls the presenter's
 *             tracks (or only the sound) and returns Realtime's offer;
 *             the browser answers. It names this connection's room
 *             ticket, which the room rations.
 *
 * Realtime's SFU is ICE-lite, so an offer is sent at once rather than
 * after candidate gathering: the browser's checks against the SFU's
 * candidates are what open the path. The camera is asked for at a size a
 * small window needs and sent in two sizes (simulcast, rids `a` and `b`),
 * so Realtime can give a viewer on a weak connection the smaller one by
 * itself; each viewer pulls it and the meeting's cost is egress.
 *
 * Both ends watch their connection: `onDrop` fires when it fails or stays
 * disconnected, for the interface to connect again. The presenter's end
 * also reads how well its picture is getting out (`quality`).
 */

/** A meeting media route's answer. */
interface Reply {
  ok: boolean
  status: number
  body: Record<string, unknown>
}

export type MediaPost = (action: 'publish' | 'stop' | 'pull' | 'answer', body: Record<string, unknown>) => Promise<Reply>

const defaultPost: MediaPost = async (action, body) => {
  const res = await fetch(`/api/meeting/media/${action}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return { ok: res.ok, status: res.status, body: (await res.json().catch(() => ({}))) as Record<string, unknown> }
}

/** Why going live or watching failed, in terms the interface can say. */
export class MeetingMediaError extends Error {
  constructor(
    readonly reason: 'denied' | 'not_live' | 'not_presenter' | 'not_in_room' | 'too_many' | 'unavailable',
    detail = '',
  ) {
    super(`${reason}${detail ? `: ${detail}` : ''}`)
  }
}

const ICE_SERVERS: RTCIceServer[] = [{ urls: 'stun:stun.cloudflare.com:3478' }]

/** What the camera is asked for: enough for a window a quarter of a phone wide. */
export const CAMERA_CONSTRAINTS: MediaTrackConstraints = { width: { ideal: 640 }, height: { ideal: 360 }, frameRate: { ideal: 24, max: 30 } }
const MIC_CONSTRAINTS: MediaTrackConstraints = { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
/** The video bitrate cap of the full size, bits per second. */
export const VIDEO_MAX_BITRATE = 600_000
/** The camera's two sizes: rid `a` full, `b` half (Realtime ranks rids a-z). */
export const SIMULCAST_ENCODINGS: RTCRtpEncodingParameters[] = [
  { rid: 'a', maxBitrate: VIDEO_MAX_BITRATE },
  { rid: 'b', maxBitrate: 150_000, scaleResolutionDownBy: 2 },
]
/** A connection that stays disconnected this long has dropped. */
export const DROP_AFTER_MS = 5000

// ---------------------------------------------------------------- devices

const DEVICE_KEYS = { video: 'ayni-meeting-camera', audio: 'ayni-meeting-mic' } as const

function localStorageOrNull(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

/** The camera and microphone this browser chose last time, if any. */
export function preferredDevices(store: Storage | null = localStorageOrNull()): { video: string | null; audio: string | null } {
  const read = (key: string): string | null => {
    try {
      return store?.getItem(key) || null
    } catch {
      return null
    }
  }
  return { video: read(DEVICE_KEYS.video), audio: read(DEVICE_KEYS.audio) }
}

/** Remember a chosen device for next time, on this browser only. */
export function rememberDevice(kind: 'audio' | 'video', deviceId: string, store: Storage | null = localStorageOrNull()): void {
  try {
    store?.setItem(DEVICE_KEYS[kind], deviceId)
  } catch {
    // Storage refused: the choice lasts as long as this page.
  }
}

type GetUserMedia = (c: MediaStreamConstraints) => Promise<MediaStream>
const defaultGetUserMedia: GetUserMedia = (c) => navigator.mediaDevices.getUserMedia(c)

function mediaFailure(err: unknown): MeetingMediaError {
  const name = err instanceof DOMException ? err.name : ''
  return new MeetingMediaError(name === 'NotAllowedError' || name === 'SecurityError' ? 'denied' : 'unavailable', name)
}

/**
 * Ask for the camera and microphone; a refusal is `denied`. The devices
 * chosen last time are preferred, not required, so an unplugged one
 * falls back to the default.
 */
export async function openCameraAndMic(
  getUserMedia: GetUserMedia = defaultGetUserMedia,
  preferred: { video: string | null; audio: string | null } = preferredDevices(),
): Promise<MediaStream> {
  try {
    return await getUserMedia({
      video: { ...CAMERA_CONSTRAINTS, ...(preferred.video ? { deviceId: { ideal: preferred.video } } : {}) },
      audio: { ...MIC_CONSTRAINTS, ...(preferred.audio ? { deviceId: { ideal: preferred.audio } } : {}) },
    })
  } catch (err) {
    throw mediaFailure(err)
  }
}

/**
 * AYNI (multi-globe plan, phase 3): the microphone alone, as a stream, for
 * a globe's driver (sound only, owner 2026-10-10); a refusal is `denied`.
 */
export async function openMic(
  getUserMedia: GetUserMedia = defaultGetUserMedia,
  preferred: string | null = preferredDevices().audio,
): Promise<MediaStream> {
  try {
    return await getUserMedia({ audio: { ...MIC_CONSTRAINTS, ...(preferred ? { deviceId: { ideal: preferred } } : {}) } })
  } catch (err) {
    throw mediaFailure(err)
  }
}

/** One new track of `kind` from `deviceId` (or the default), at the meeting's settings. */
export async function openDevice(
  kind: 'audio' | 'video',
  deviceId: string | null,
  getUserMedia: GetUserMedia = defaultGetUserMedia,
): Promise<MediaStreamTrack> {
  const base = kind === 'video' ? CAMERA_CONSTRAINTS : MIC_CONSTRAINTS
  const constraints = { ...base, ...(deviceId ? { deviceId: { exact: deviceId } } : {}) }
  try {
    const stream = await getUserMedia(kind === 'video' ? { video: constraints } : { audio: constraints })
    const track = kind === 'video' ? stream.getVideoTracks()[0] : stream.getAudioTracks()[0]
    if (!track) throw new MeetingMediaError('unavailable', 'no track')
    return track
  } catch (err) {
    throw err instanceof MeetingMediaError ? err : mediaFailure(err)
  }
}

/** The cameras and microphones this browser can name (names appear once a device has been allowed). */
export async function listDevices(
  enumerate: () => Promise<MediaDeviceInfo[]> = () => navigator.mediaDevices.enumerateDevices(),
): Promise<{ video: MediaDeviceInfo[]; audio: MediaDeviceInfo[] }> {
  const all = await enumerate().catch(() => [] as MediaDeviceInfo[])
  return {
    video: all.filter((d) => d.kind === 'videoinput' && d.deviceId),
    audio: all.filter((d) => d.kind === 'audioinput' && d.deviceId),
  }
}

// ------------------------------------------------------------ connections

/** How well the presenter's picture is getting out. */
export type Quality = 'good' | 'weak' | 'poor'

type StatsLike = { forEach(fn: (report: Record<string, unknown>) => void): void }

/**
 * Read a publishing connection's stats: `poor` when it is not connected,
 * losing more than a tenth of its packets, a second behind, or left with
 * too little bandwidth for even the small picture; `weak` when the
 * browser is holding the picture back for bandwidth or the connection is
 * lossy or slow; `good` otherwise.
 */
export function connectionQuality(stats: StatsLike, state: RTCPeerConnectionState): Quality {
  if (state !== 'connected') return state === 'new' || state === 'connecting' ? 'good' : 'poor'
  let rtt: number | null = null
  let lost = 0
  let available: number | null = null
  let limited = false
  stats.forEach((r) => {
    const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)
    if (r.type === 'candidate-pair' && r.state === 'succeeded' && (r.nominated === true || r.selected === true)) {
      rtt = num(r.currentRoundTripTime) ?? rtt
      available = num(r.availableOutgoingBitrate) ?? available
    } else if (r.type === 'remote-inbound-rtp') {
      lost = Math.max(lost, num(r.fractionLost) ?? 0)
      rtt ??= num(r.roundTripTime)
    } else if (r.type === 'outbound-rtp' && r.kind === 'video' && r.qualityLimitationReason === 'bandwidth') {
      limited = true
    }
  })
  const r = rtt as number | null
  const a = available as number | null
  if (lost > 0.1 || (r !== null && r > 1) || (a !== null && a < 150_000)) return 'poor'
  if (lost > 0.03 || (r !== null && r > 0.4) || limited || (a !== null && a < 450_000)) return 'weak'
  return 'good'
}

/**
 * Call `onDrop` once when `pc` fails, stays disconnected for
 * DROP_AFTER_MS, or closes without `isClosing()` saying it was us.
 */
function watchForDrop(pc: RTCPeerConnection, isClosing: () => boolean, onDrop: () => void): void {
  let timer: ReturnType<typeof setTimeout> | null = null
  let dropped = false
  const drop = (): void => {
    if (dropped || isClosing()) return
    dropped = true
    onDrop()
  }
  pc.addEventListener('connectionstatechange', () => {
    const state = pc.connectionState
    if (timer !== null && state !== 'disconnected') {
      clearTimeout(timer)
      timer = null
    }
    if (state === 'failed' || state === 'closed') drop()
    else if (state === 'disconnected' && timer === null) {
      timer = setTimeout(() => {
        timer = null
        if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') drop()
      }, DROP_AFTER_MS)
    }
  })
}

// ---------------------------------------------------------------- presenter

export interface LiveHandle {
  /** Whether the camera goes out in two sizes. */
  simulcast: boolean
  /** Put a new camera or microphone track in place of the one of its kind, without renegotiating. */
  replaceTrack(track: MediaStreamTrack): Promise<void>
  /** How well the picture is getting out right now. */
  quality(): Promise<Quality>
  /** Called once if the connection drops. */
  onDrop(listener: () => void): void
  /** End this publication (the stream's tracks are the caller's to stop). */
  close(): void
}

function trackName(kind: string): string {
  const bytes = crypto.getRandomValues(new Uint8Array(8))
  return `${kind}-${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`
}

function failure(reply: Reply): MeetingMediaError {
  const code = typeof reply.body.error === 'string' ? reply.body.error : ''
  if (reply.status === 404 || code === 'not_live') return new MeetingMediaError('not_live')
  if (code === 'not_presenter') return new MeetingMediaError('not_presenter')
  if (code === 'not_in_room') return new MeetingMediaError('not_in_room')
  if (reply.status === 429) return new MeetingMediaError('too_many')
  return new MeetingMediaError('unavailable', `${reply.status} ${code}`)
}

/** Add the camera in two sizes; a browser that refuses simulcast gets one size, capped. */
async function addCamera(pc: RTCPeerConnection, track: MediaStreamTrack, stream: MediaStream): Promise<{ transceiver: RTCRtpTransceiver; simulcast: boolean }> {
  try {
    const transceiver = pc.addTransceiver(track, { direction: 'sendonly', streams: [stream], sendEncodings: SIMULCAST_ENCODINGS })
    return { transceiver, simulcast: true }
  } catch {
    const transceiver = pc.addTransceiver(track, { direction: 'sendonly', streams: [stream] })
    const params = transceiver.sender.getParameters()
    params.encodings = [{ ...(params.encodings?.[0] ?? {}), maxBitrate: VIDEO_MAX_BITRATE }]
    await transceiver.sender.setParameters(params).catch(() => {
      // Not every browser takes encodings before negotiation; the cap is a nicety.
    })
    return { transceiver, simulcast: false }
  }
}

/**
 * AYNI (multi-globe plan, phase 3): who is publishing — the presenter
 * (their signed link, `st`) or the driver of a globe (their room ticket,
 * which the room checks against the globe's driver; microphone only).
 */
export type Publisher = { kind: 'presenter'; token: string } | { kind: 'driver'; slot: MicSlot; ticket: string }

/** A driver's microphone: a globe by its position, or (AYNI, owner 2026-10-12) `spot`, the main globe handed to a participant. */
export type MicSlot = number | 'spot'

/** How the room names a driver's microphone. */
export const micPublisher = (slot: MicSlot): string => (slot === 'spot' ? 'spot' : `slot:${slot}`)

/** What a publisher puts in a media request: the presenter's link, or the driver's ticket and globe. */
function publisherBody(p: Publisher): Record<string, string> {
  return p.kind === 'presenter' ? { st: p.token } : { ticket: p.ticket, publisher: micPublisher(p.slot) }
}

/** Publish `stream` as room `room`'s presenter (`token`: the presenter's signed link), or as a globe's driver (`as`). */
export async function goLive(
  room: string,
  token: string,
  stream: MediaStream,
  post: MediaPost = defaultPost,
  Peer: typeof RTCPeerConnection = RTCPeerConnection,
  as: Publisher = { kind: 'presenter', token },
): Promise<LiveHandle> {
  const pc = new Peer({ iceServers: ICE_SERVERS, bundlePolicy: 'max-bundle' })
  let closing = false
  let simulcast = false
  const transceivers: RTCRtpTransceiver[] = []
  try {
    for (const track of stream.getTracks()) {
      if (track.kind === 'video') {
        const added = await addCamera(pc, track, stream)
        simulcast = added.simulcast
        transceivers.push(added.transceiver)
      } else {
        transceivers.push(pc.addTransceiver(track, { direction: 'sendonly', streams: [stream] }))
      }
    }
    await pc.setLocalDescription(await pc.createOffer())
    // Mids exist only once the offer is the local description.
    const tracks = transceivers.map((t) => ({ mid: t.mid ?? '', trackName: trackName(t.sender.track?.kind ?? 'track') }))
    if (tracks.some((t) => t.mid === '')) throw new MeetingMediaError('unavailable', 'no mid')
    const reply = await post('publish', { room, ...publisherBody(as), offer: pc.localDescription?.sdp ?? '', tracks, simulcast })
    if (!reply.ok || typeof reply.body.answer !== 'string') throw failure(reply)
    await pc.setRemoteDescription({ type: 'answer', sdp: reply.body.answer })
  } catch (err) {
    pc.close()
    throw err instanceof MeetingMediaError ? err : new MeetingMediaError('unavailable', String(err))
  }
  const dropListeners: (() => void)[] = []
  watchForDrop(pc, () => closing, () => dropListeners.forEach((fn) => fn()))
  return {
    simulcast,
    async replaceTrack(track) {
      const sender = transceivers.find((t) => t.sender.track?.kind === track.kind || t.receiver.track?.kind === track.kind)?.sender
      if (!sender) throw new MeetingMediaError('unavailable', `no ${track.kind} sender`)
      await sender.replaceTrack(track)
    },
    async quality() {
      return connectionQuality(await pc.getStats(), pc.connectionState)
    },
    onDrop(listener) {
      dropListeners.push(listener)
    },
    close() {
      closing = true
      pc.close()
    },
  }
}

/** Tell the room the presenter (or a globe's driver, `as`) stopped. */
export async function stopLive(room: string, token: string, post: MediaPost = defaultPost, as: Publisher = { kind: 'presenter', token }): Promise<void> {
  await post('stop', { room, ...publisherBody(as) }).catch(() => undefined)
}

// ----------------------------------------------------------------- everyone

export interface WatchHandle {
  /** The presenter's camera and microphone (or only the microphone). */
  stream: MediaStream
  /** The media revision this is. */
  rev: number | null
  /** Called once if the connection drops. */
  onDrop(listener: () => void): void
  close(): void
}

/** Pull room `room`'s presenter, as the connection holding `ticket`; `audioOnly` leaves the picture out. */
export async function watch(
  room: string,
  ticket: string,
  /** `slot`: pull a globe's driver (their microphone) instead of the presenter; `spot`, the main globe's driver's. */
  opts: { audioOnly?: boolean; slot?: MicSlot } = {},
  post: MediaPost = defaultPost,
  Peer: typeof RTCPeerConnection = RTCPeerConnection,
): Promise<WatchHandle> {
  const pulled = await post('pull', { room, ticket, ...(opts.audioOnly ? { audioOnly: true } : {}), ...(opts.slot !== undefined ? { publisher: micPublisher(opts.slot) } : {}) })
  if (!pulled.ok || typeof pulled.body.offer !== 'string' || typeof pulled.body.sessionId !== 'string') throw failure(pulled)
  const pc = new Peer({ iceServers: ICE_SERVERS, bundlePolicy: 'max-bundle' })
  let closing = false
  const stream = new MediaStream()
  pc.addEventListener('track', (ev) => {
    if (!stream.getTracks().includes(ev.track)) stream.addTrack(ev.track)
  })
  try {
    await pc.setRemoteDescription({ type: 'offer', sdp: pulled.body.offer })
    await pc.setLocalDescription(await pc.createAnswer())
    const answered = await post('answer', {
      room,
      sessionId: pulled.body.sessionId,
      token: pulled.body.token,
      answer: pc.localDescription?.sdp ?? '',
    })
    if (!answered.ok) throw failure(answered)
  } catch (err) {
    pc.close()
    throw err instanceof MeetingMediaError ? err : new MeetingMediaError('unavailable', String(err))
  }
  const dropListeners: (() => void)[] = []
  watchForDrop(pc, () => closing, () => dropListeners.forEach((fn) => fn()))
  return {
    stream,
    rev: typeof pulled.body.rev === 'number' ? pulled.body.rev : null,
    onDrop(listener) {
      dropListeners.push(listener)
    },
    close() {
      closing = true
      pc.close()
      for (const track of stream.getTracks()) track.stop()
    },
  }
}
